/**
 * Firestore wishlist service layer.
 *
 * Data model:
 *   wishlists/{userId}/items/{listingId}
 *
 * Document ID equals listingId to guarantee idempotent adds and O(1) lookups.
 */

import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  writeBatch,
} from 'firebase/firestore';

import { getFirestoreDb } from '../../firebase/app';
import { WISHLIST_PAGE_SIZE } from '../../firebase/config';
import { buildWishlistItemFromListing } from './wishlistHelpers';

const itemsCollection = (db, userId) => collection(db, 'wishlists', userId, 'items');

const itemDocRef = (db, userId, listingId) => doc(db, 'wishlists', userId, 'items', listingId);

const withDb = fn => {
  const db = getFirestoreDb();
  if (!db) {
    return Promise.reject(new Error('Firebase is not configured'));
  }
  return fn(db);
};

const withRetry = async (operation, retries = 2) => {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await operation();
    } catch (err) {
      lastError = err;
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
      }
    }
  }
  throw lastError;
};

/**
 * @param {string} userId
 * @param {Object} listing
 */
export const addToWishlist = (userId, listing) =>
  withRetry(() =>
    withDb(async db => {
      const listingId = listing?.id?.uuid;
      if (!listingId) {
        throw new Error('Listing id is required');
      }
      const payload = buildWishlistItemFromListing(listing, userId);
      await setDoc(itemDocRef(db, userId, listingId), payload, { merge: true });
      return payload;
    })
  );

/**
 * @param {string} userId
 * @param {string} listingId
 */
export const removeFromWishlist = (userId, listingId) =>
  withRetry(() =>
    withDb(db => deleteDoc(itemDocRef(db, userId, listingId)))
  );

/**
 * @param {string} userId
 * @param {string} listingId
 */
export const isWishlisted = (userId, listingId) =>
  withDb(async db => {
    const snap = await getDoc(itemDocRef(db, userId, listingId));
    return snap.exists();
  });

/**
 * @param {string} userId
 * @param {Object} [options]
 * @param {number} [options.pageSize]
 * @param {import('firebase/firestore').QueryDocumentSnapshot} [options.startAfterDoc]
 */
export const getUserWishlist = (userId, options = {}) =>
  withDb(async db => {
    const pageSize = options.pageSize || WISHLIST_PAGE_SIZE;
    const constraints = [orderBy('createdAt', 'desc'), limit(pageSize)];
    if (options.startAfterDoc) {
      constraints.push(startAfter(options.startAfterDoc));
    }
    const q = query(itemsCollection(db, userId), ...constraints);
    const snapshot = await getDocs(q);
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
    return {
      items,
      lastDoc,
      hasMore: snapshot.docs.length === pageSize,
    };
  });

/**
 * Realtime listener for the user's wishlist (first page).
 * @param {string} userId
 * @param {(payload: { items: Object[], listingIds: string[] }) => void} onChange
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeToUserWishlist = (userId, onChange, onError, pageSize = WISHLIST_PAGE_SIZE) => {
  const db = getFirestoreDb();
  if (!db) {
    onError?.(new Error('Firebase is not configured'));
    return () => {};
  }

  const q = query(itemsCollection(db, userId), orderBy('createdAt', 'desc'), limit(pageSize));

  return onSnapshot(
    q,
    snapshot => {
      const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const listingIds = items.map(i => i.listingId).filter(Boolean);
      onChange({ items, listingIds });
    },
    err => onError?.(err)
  );
};

/**
 * Batch-merge anonymous items after login (max 500 per Firestore batch).
 * @param {string} userId
 * @param {Object[]} anonymousItems
 */
export const mergeAnonymousWishlist = async (userId, anonymousItems) => {
  if (!anonymousItems?.length) {
    return;
  }
  return withDb(async db => {
    const batch = writeBatch(db);
    let count = 0;
    anonymousItems.forEach(item => {
      if (!item?.listingId || count >= 500) {
        return;
      }
      const ref = itemDocRef(db, userId, item.listingId);
      batch.set(
        ref,
        {
          ...item,
          userId,
          createdAt: item.createdAt || new Date().toISOString(),
        },
        { merge: true }
      );
      count += 1;
    });
    if (count > 0) {
      await batch.commit();
    }
  });
};
